import Client from 'client';
import gql from 'graphql-tag';
import { CountryCode } from 'modules/company/types/company.type';
import { CompanyFormFields } from 'modules/company/types/companyFormFields.type';
import { ToastError } from 'utils';
import * as Yup from 'yup';

const isExistingName = async (name?: string | null): Promise<boolean> => {
  return Client.query({
    query: gql`
      query ($name: String!) {
        company(where: { name: { _eq: $name } }) {
          id
          name
        }
      }
    `,
    variables: { name: name || '' },
  }).then(
    (response) => response.data.company && response.data.company.length > 0,
  );
};

const isExistingRegistrationNumber = async (
  registrationNumber?: string | null,
): Promise<boolean> => {
  return Client.query({
    query: gql`
      query ($registrationNumber: String!) {
        company(where: { registrationNumber: { _eq: $registrationNumber } }) {
          id
        }
      }
    `,
    variables: { registrationNumber: registrationNumber || '' },
  }).then(
    (response) => response.data.company && response.data.company.length > 0,
  );
};

export const companyFormValidationSchema = (props?: {
  currentName?: string | null;
  currentRegistrationNumber?: string | null;
}): Yup.ObjectSchema<CompanyFormFields> =>
  Yup.object({
    name: Yup.string()
      .required('Le nom est requis !')
      .test(
        'is-name-uniq',
        `ATTENTION ! Ce nom d'entreprise existe déjà dans notre base, veuillez assigner l'entreprise existante à la fiche ou créez l'entreprise manuellement en changeant le nom`,
        async (name) => {
          if (props?.currentName !== name) {
            return isExistingName(name)
              .then((res) => res === false)
              .catch((error) => {
                ToastError(
                  'Erreur',
                  "Impossible de vérifier l'unicité du nom !",
                );
                throw error;
              });
          }
          return true;
        },
      )
      .when(['countryCode', 'legalForm'], {
        is: (countryCode: string, legalForm: string) =>
          countryCode === CountryCode.FR &&
          legalForm === 'Entrepreneur individuel',
        then: (schema) =>
          schema.test(
            'ei-in-name',
            'La mention (EI) est obligatoire en fin de nom pour un Entrepreneur individuel',
            (name) => name?.includes('(EI)') ?? false,
          ),
      }),
    address: Yup.object({
      streetAddress: Yup.string().required("L'adresse est requise !"),
      postalCode: Yup.string().required('Le code postal est requis !'),
      city: Yup.string().required('La ville est requise !'),
    }),
    formattedAddress: Yup.string()
      .required("L'adresse est requise !")
      .test(
        'address-filled',
        "Veuillez sélectionner une adresse valide parmi les propositions ou remplir manuellement via le bouton d'édition",
        function () {
          const { address } = this.parent;
          return (
            !!address?.streetAddress && !!address?.postalCode && !!address?.city
          );
        },
      ),
    billingEmail: Yup.string()
      .required('Adresse email de facturation requise !')
      .email('Email invalide !'),
    registrationNumber: Yup.string()
      .trim()
      .required("Le numéro d'enregistrement est requis")
      .test(
        'is-registration-number-unique',
        `ATTENTION ! Cette entreprise existe déjà dans notre base, veuillez assigner l'entreprise existante à la fiche`,
        async (registrationNumber) => {
          if (props?.currentRegistrationNumber !== registrationNumber) {
            return isExistingRegistrationNumber(registrationNumber)
              .then((res) => res === false)
              .catch((error) => {
                ToastError(
                  'Erreur',
                  "Impossible de vérifier l'unicité du numéro d'entreprise !",
                );
                throw error;
              });
          }
          return true;
        },
      )
      .when('countryCode', {
        is: 'FR',
        then: (schema) =>
          schema.length(14, 'Un SIRET français fait 14 chiffres !'),
      })
      .when('countryCode', {
        is: 'BE',
        then: (schema) =>
          schema.length(10, "Un numéro d'entreprise belge fait 10 chiffres !"),
      })
      .required("Le numéro d'enregistrement est requis"),
    vatNumber: Yup.string()
      .trim()
      .when('countryCode', {
        is: 'FR',
        then: (schema) =>
          schema
            .length(13, 'Le numéro de TVA français doit faire 13 caractères !')
            .matches(
              /^FR\d{11}$/,
              'Le numéro de TVA est invalide, FR + 11 chiffres !',
            ),
      })
      .when('countryCode', {
        is: 'BE',
        then: (schema) =>
          schema
            .length(12, 'Le numéro de TVA belge doit faire 12 caractères !')
            .matches(
              /^BE[01]\d{9}$/,
              'Le numéro de TVA est invalide, BE + 10 chiffres !',
            ),
      })
      .required('Le numéro TVA est requis'),
    legalForm: Yup.string().required("Le statut de l'entreprise est requis !"),
    countryCode: Yup.mixed<CountryCode>()
      .oneOf(Object.values(CountryCode), 'Pays non valide')
      .required('Le code pays est requis'),
  });
